import React, { useState, useEffect } from 'react';
import {
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Box, Button, TablePagination
} from '@mui/material';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';

const LocationAnalytics = () => {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchProject, setSearchProject] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch scenes with locations
      const { data: scenesData, error: scenesError } = await supabase
        .from('scene')
        .select(`
          scenes_id,
          location_name,
          shoot_location,
          scene_num,
          created_at,
          project:project (
            project_id,
            project_name
          )
        `)
        .order('created_at', { ascending: false });

      if (scenesError) throw scenesError;

      // Group locations by project_name and location_name
      const groupedLocations = scenesData.reduce((acc, scene) => {
        const key = `${scene.project?.project_name}-${scene.location_name}`;
        
        if (!acc[key]) {
          acc[key] = {
            scenes_id: scene.scenes_id,
            created_at: scene.created_at,
            project_name: scene.project?.project_name || 'Unknown Project',
            scene_num: scene.scene_num,
            location_name: scene.location_name,
            shoot_location: scene.shoot_location,
            scenes_ids: [scene.scenes_id]
          };
        } else {
          acc[key].scenes_ids.push(scene.scenes_id);
          // Keep the earliest creation date
          if (new Date(scene.created_at) < new Date(acc[key].created_at)) {
            acc[key].created_at = scene.created_at;
          }
          // If shoot_location is different, combine them
          if (scene.shoot_location && 
              !acc[key].shoot_location?.includes(scene.shoot_location)) {
            acc[key].shoot_location = acc[key].shoot_location 
              ? `${acc[key].shoot_location}, ${scene.shoot_location}`
              : scene.shoot_location;
          }
        }
        return acc;
      }, {});

      // Convert grouped object back to array
      const combinedLocations = Object.values(groupedLocations)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setLocations(combinedLocations);
      setFilteredLocations(combinedLocations);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSearch = () => {
    let filtered = locations;

    if (searchProject) {
      filtered = filtered.filter(location =>
        location.project_name?.toLowerCase().includes(searchProject.toLowerCase())
      );
    }

    if (searchLocation) {
      filtered = filtered.filter(location =>
        location.location_name?.toLowerCase().includes(searchLocation.toLowerCase()) ||
        location.shoot_location?.toLowerCase().includes(searchLocation.toLowerCase())
      );
    }

    setFilteredLocations(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedLocations = filteredLocations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="location-analytics">
      <h2>Location Analytics</h2>

      {/* Search Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by Project"
          value={searchProject}
          onChange={(e) => setSearchProject(e.target.value)}
        />
        <TextField
          label="Search by Location"
          value={searchLocation}
          onChange={(e) => setSearchLocation(e.target.value)}
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Locations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              <TableCell>Created On</TableCell>
              <TableCell>Project Name</TableCell>
              <TableCell>Scene Number</TableCell>
              <TableCell>Script Location Name</TableCell>
              <TableCell>Shoot Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLocations.map((location, index) => (
              <TableRow key={location.scenes_id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>
                  {format(new Date(location.created_at), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>{location.project_name}</TableCell>
                <TableCell>{location.scene_num}</TableCell>
                <TableCell>{location.location_name}</TableCell>
                <TableCell>{location.shoot_location}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredLocations.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20]}
        />
      </TableContainer>
    </div>
  );
};

export default LocationAnalytics; 